import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
} from '@mui/icons-material';

import { maintenanceService } from '../../services/maintenanceService';
import { useSiteContext } from '../../hooks/useSiteContext';
import {
  MaintenanceSchedule,
  CreateMaintenanceSchedule,
  MaintenanceScheduleFilters,
  PaginatedResponse,
  FrequencyType,
} from '../../types/maintenance';

const MaintenanceSchedules: React.FC = () => {
  const { currentSite } = useSiteContext();
  const [schedules, setSchedules] = useState<MaintenanceSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<MaintenanceSchedule | null>(null);
  const [scheduleForm, setScheduleForm] = useState<Partial<CreateMaintenanceSchedule>>({
    equipment: 0,
    standard_task: 0,
    frequency_type: FrequencyType.MONTHLY,
    frequency_value: 1,
    next_due_date: '',
    advance_notice_days: 7,
    grace_period_days: 3,
  });

  // Filtering
  const [filters, setFilters] = useState<MaintenanceScheduleFilters>({});

  // Load schedules
  const loadSchedules = useCallback(async () => {
    if (!currentSite.siteId) return;

    try {
      setLoading(true);
      setError(null);

      const searchFilters: MaintenanceScheduleFilters = {
        ...filters,
        site: currentSite.siteId.toString(),
        page: page + 1,
        page_size: rowsPerPage,
      };

      const response: PaginatedResponse<MaintenanceSchedule> = 
        await maintenanceService.schedules.getSchedules(searchFilters);

      setSchedules(response.results || []);
      setTotalCount(response.count || 0);
    } catch (err) {
      console.error('Failed to load schedules:', err);
      setError('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  }, [currentSite.siteId, page, rowsPerPage, filters]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  const handleCreateSchedule = async () => {
    try {
      await maintenanceService.schedules.createSchedule(scheduleForm as CreateMaintenanceSchedule);
      setCreateDialogOpen(false);
      resetForm();
      loadSchedules();
    } catch (err) {
      console.error('Failed to create schedule:', err);
    }
  };

  const handleEditSchedule = async () => {
    if (!selectedSchedule) return;

    try {
      await maintenanceService.schedules.updateSchedule(selectedSchedule.id, scheduleForm);
      setEditDialogOpen(false);
      resetForm();
      loadSchedules();
    } catch (err) {
      console.error('Failed to update schedule:', err);
    }
  };

  const handleDeleteSchedule = async (id: number) => {
    try {
      await maintenanceService.schedules.deleteSchedule(id);
      loadSchedules();
    } catch (err) {
      console.error('Failed to delete schedule:', err);
    }
  };

  const handleToggleSchedule = async (schedule: MaintenanceSchedule) => {
    try {
      // Note: is_active toggle would need to be handled by backend API directly
      // For now, we'll reload schedules to reflect any changes
      loadSchedules();
    } catch (err) {
      console.error('Failed to toggle schedule:', err);
    }
  };

  const resetForm = () => {
    setScheduleForm({
      equipment: 0,
      standard_task: 0,
      frequency_type: FrequencyType.MONTHLY,
      frequency_value: 1,
      next_due_date: '',
      advance_notice_days: 7,
      grace_period_days: 3,
    });
    setSelectedSchedule(null);
  };

  const openEditDialog = (schedule: MaintenanceSchedule) => {
    setSelectedSchedule(schedule);
    setScheduleForm({
      equipment: schedule.equipment,
      standard_task: schedule.standard_task,
      frequency_type: schedule.frequency_type,
      frequency_value: schedule.frequency_value,
      next_due_date: schedule.next_due_date,
      advance_notice_days: schedule.advance_notice_days,
      grace_period_days: schedule.grace_period_days,
      hours_based_trigger: schedule.hours_based_trigger,
      cycles_based_trigger: schedule.cycles_based_trigger,
    });
    setEditDialogOpen(true);
  };

  const getFrequencyDisplay = (frequency: string, value: number) => {
    return `Every ${value} ${frequency}${value > 1 ? 's' : ''}`;
  };

  const getStatusColor = (schedule: MaintenanceSchedule) => {
    if (!schedule.is_active) return 'default';
    
    const nextDue = new Date(schedule.next_due_date);
    const now = new Date();
    const daysDiff = Math.ceil((nextDue.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysDiff < 0) return 'error'; // Overdue
    if (daysDiff <= 7) return 'warning'; // Due soon
    return 'success'; // On time
  };

  const getStatusText = (schedule: MaintenanceSchedule) => {
    if (!schedule.is_active) return 'Inactive';
    
    const nextDue = new Date(schedule.next_due_date);
    const now = new Date();
    const daysDiff = Math.ceil((nextDue.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysDiff < 0) return `Overdue by ${Math.abs(daysDiff)} days`;
    if (daysDiff <= 7) return `Due in ${daysDiff} days`;
    return `Due in ${daysDiff} days`;
  };

  if (!currentSite.siteId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a site to view maintenance schedules.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Maintenance Schedules</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Schedule
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.is_active !== undefined ? filters.is_active.toString() : ''}
                  label="Status"
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilters({ 
                      ...filters, 
                      is_active: value === '' ? undefined : value === 'true'
                    });
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={filters.frequency_type || ''}
                  label="Frequency"
                  onChange={(e) => setFilters({ ...filters, frequency_type: e.target.value as FrequencyType })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="annually">Annually</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Due Date From"
                InputLabelProps={{ shrink: true }}
                onChange={(e) => {
                  const value = e.target.value;
                  // Using due_within_days as an approximation
                  const daysFromNow = Math.ceil((new Date(value).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                  setFilters({ ...filters, due_within_days: daysFromNow > 0 ? daysFromNow : undefined });
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Due Date To"
                InputLabelProps={{ shrink: true }}
                onChange={(e) => {
                  // Due date "to" filtering isn't directly supported in the API
                  // Could be implemented with search filters or additional API support
                  console.log('Due date to filter not implemented:', e.target.value);
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Schedules Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Equipment</TableCell>
                    <TableCell>Frequency</TableCell>
                    <TableCell>Next Due</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedules.map((schedule) => (
                    <TableRow key={schedule.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {schedule.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {schedule.description}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {schedule.equipment_name || 'Not assigned'}
                      </TableCell>
                      <TableCell>
                        {getFrequencyDisplay(schedule.frequency_type, schedule.frequency_value)}
                      </TableCell>
                      <TableCell>
                        {new Date(schedule.next_due_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(schedule)}
                          color={getStatusColor(schedule) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={schedule.is_active}
                          onChange={() => handleToggleSchedule(schedule)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Tooltip title="Edit Schedule">
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(schedule)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Details">
                            <IconButton size="small">
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Schedule">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteSchedule(schedule.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {schedules.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No schedules found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Paper>

      {/* Create Schedule Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Maintenance Schedule</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Frequency Type</InputLabel>
                <Select
                  value={scheduleForm.frequency_type}
                  label="Frequency Type"
                  onChange={(e) => setScheduleForm({ ...scheduleForm, frequency_type: e.target.value as FrequencyType })}
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="annually">Annually</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Frequency Value"
                value={scheduleForm.frequency_value}
                onChange={(e) => setScheduleForm({ 
                  ...scheduleForm, 
                  frequency_value: parseInt(e.target.value) 
                })}
                inputProps={{ min: 1 }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Next Due Date"
                InputLabelProps={{ shrink: true }}
                onChange={(e) => setScheduleForm({ ...scheduleForm, next_due_date: e.target.value })}
                required
              />
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateSchedule} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Schedule Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Maintenance Schedule</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Frequency Type</InputLabel>
                <Select
                  value={scheduleForm.frequency_type}
                  label="Frequency Type"
                  onChange={(e) => setScheduleForm({ ...scheduleForm, frequency_type: e.target.value as FrequencyType })}
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="annually">Annually</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Frequency Value"
                value={scheduleForm.frequency_value}
                onChange={(e) => setScheduleForm({ 
                  ...scheduleForm, 
                  frequency_value: parseInt(e.target.value) 
                })}
                inputProps={{ min: 1 }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Next Due Date"
                value={scheduleForm.next_due_date}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => setScheduleForm({ ...scheduleForm, next_due_date: e.target.value })}
                required
              />
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSchedule} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaintenanceSchedules; 